import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

// ── Baseline ───────────────────────────────────────────────────────────────
const BASELINE = {
  spf:   'v=spf1 include:deem.sa -all',
  mx:    ['mx1.deem.sa', 'mx2.deem.sa', 'mx3.deem.sa', 'mx4.deem.sa'],
  dmarc: 'v=DMARC1; p=reject; fo=1; rua=mailto:haseen@rua.dmarc.gov.sa,mailto:security@deem.sa,mailto:soc@nic.gov.sa; ruf=mailto:haseen@ruf.dmarc.gov.sa,mailto:security@deem.sa,mailto:soc@nic.gov.sa;',
};

// ── Check if monitor has been checked ──────────────────────────────────────
function hasBeenChecked(monitor) {
  // Check multiple possible fields
  if (monitor.last_check_at) return true;
  if (monitor.last_checked) return true;
  if (monitor.spf_status && monitor.spf_status !== 'pending') return true;
  if (monitor.spf_current || monitor.spf_expected) return true;
  return false;
}

// ── Smart analysis ─────────────────────────────────────────────────────────
function analyzeSpf(current, storedStatus) {
  if (storedStatus && storedStatus !== 'pending') return storedStatus;
  if (!current) return 'missing';
  if (current === BASELINE.spf) return 'match';
  if (!current.includes('include:deem.sa')) return 'mismatch';
  return 'warning';
}

function analyzeMx(current, storedStatus) {
  if (storedStatus && storedStatus !== 'pending') return storedStatus;
  if (!current) return 'missing';
  var lc = current.toLowerCase();
  var allPresent = BASELINE.mx.every(function(m) { return lc.includes(m); });
  if (allPresent) return 'match';
  if (BASELINE.mx.some(function(m) { return lc.includes(m); })) return 'warning';
  return 'mismatch';
}

function analyzeDmarc(current, storedStatus) {
  if (storedStatus && storedStatus !== 'pending') return storedStatus;
  if (!current) return 'missing';
  if (current === BASELINE.dmarc) return 'match';
  if (!current.startsWith('v=DMARC1')) return 'mismatch';
  return 'warning';
}

function analyzeDkim(current, storedStatus) {
  if (storedStatus && storedStatus !== 'pending') return storedStatus;
  if (!current) return 'missing';
  return 'match';
}

// ── Status display helpers ─────────────────────────────────────────────────
var STATUS_META = {
  match:    { label: 'Match',    cls: 'status-match'   },
  found:    { label: 'Found',    cls: 'status-match'   },
  warning:  { label: 'Warning',  cls: 'status-warning' },
  mismatch: { label: 'Mismatch', cls: 'status-down'    },
  missing:  { label: 'Missing',  cls: 'status-down'    },
  revoked:  { label: 'Revoked',  cls: 'status-down'    },
  pending:  { label: 'Pending',  cls: 'status-neutral' },
  unknown:  { label: '-',        cls: 'status-neutral' },
};

function StatusCell({ status }) {
  var meta = STATUS_META[status] || STATUS_META.unknown;
  return (
    <span
      className={'status-badge ' + meta.cls}
      style={{ fontSize: '11px', padding: '2px 7px' }}
    >
      <span className="badge-dot" />
      {meta.label}
    </span>
  );
}

function HealthScore({ score }) {
  if (score === undefined || score === null) {
    return <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>-</span>;
  }
  // Handle both 0-4 (new float) and 0-100 (legacy) scale
  var displayScore = score;
  if (score > 4) {
    displayScore = Math.round(score / 25);
  }
  // Show one decimal place only when needed (e.g. 3.5, 2.5)
  var formatted = Number.isInteger(displayScore) ? displayScore : displayScore.toFixed(1);
  var color = displayScore >= 4 ? 'var(--success)' : displayScore >= 2 ? 'var(--warning)' : 'var(--danger)';
  return (
    <span style={{
      fontWeight: 700, fontSize: '13px', color: color,
      fontFamily: 'var(--font-mono)',
    }}>
      {formatted}/4
    </span>
  );
}

// ── Icons ──────────────────────────────────────────────────────────────────
var IconPlus    = function() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; };
var IconTrash   = function() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>; };
var IconSearch  = function() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; };
var IconRefresh = function() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>; };
var IconGlobe   = function() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>; };
var IconChevron = function({ open }) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>; };

// ── DNS Change History Section ─────────────────────────────────────────────
function HistorySection({ monitorId }) {
  var [open, setOpen] = useState(false);
  var [history, setHistory] = useState([]);
  var [histLoading, setHistLoading] = useState(false);
  var [fetched, setFetched] = useState(false);

  function toggle() {
    if (!open && !fetched) {
      setHistLoading(true);
      api.get('/dns/' + monitorId + '/history')
        .then(function(res) {
          setHistory(res.data);
          setFetched(true);
        })
        .catch(function(err) {
          console.error('Failed to fetch DNS history:', err);
          setFetched(true);
        })
        .finally(function() {
          setHistLoading(false);
        });
    }
    setOpen(function(prev) { return !prev; });
  }

  function relTime(dateStr) {
    if (!dateStr) return '-';
    var diff = Date.now() - new Date(dateStr).getTime();
    var mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return mins + 'm ago';
    var hrs = Math.floor(mins / 60);
    if (hrs < 24) return hrs + 'h ago';
    return Math.floor(hrs / 24) + 'd ago';
  }

  var typeColors = {
    SPF:   { bg: 'var(--primary-subtle)', border: 'var(--primary-border)', text: 'var(--primary)' },
    MX:    { bg: 'var(--primary-subtle)', border: 'var(--primary-border)', text: 'var(--primary)' },
    DMARC: { bg: 'var(--primary-subtle)', border: 'var(--primary-border)', text: 'var(--primary)' },
    DKIM:  { bg: 'var(--primary-subtle)', border: 'var(--primary-border)', text: 'var(--primary)' },
  };

  return (
    <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      <button
        onClick={toggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600,
          fontFamily: 'var(--font)', padding: 0,
        }}
      >
        <IconChevron open={open} />
        DNS Change History
      </button>

      {open && (
        <div style={{ marginTop: 10 }}>
          {histLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0',
                          color: 'var(--text-muted)', fontSize: 12 }}>
              <div className="spinner" style={{ width: 14, height: 14 }} /> Loading history…
            </div>
          ) : history.length === 0 ? (
            <div style={{
              color: 'var(--text-muted)', fontSize: 12, padding: '10px 14px',
              background: 'var(--bg-table-head)', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
            }}>
              No DNS changes recorded yet. Changes appear automatically when a record value differs between two consecutive checks.
            </div>
          ) : (
            <div style={{
              border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
            }}>
              {history.map(function(ch, i) {
                var tc = typeColors[ch.record_type] || typeColors.SPF;
                return (
                  <div key={ch.id || i} style={{
                    padding: '10px 14px',
                    background: i % 2 === 0 ? 'var(--bg-card)' : 'var(--bg-table-head)',
                    borderBottom: i < history.length - 1 ? '1px solid var(--border)' : 'none',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                  marginBottom: 6, flexWrap: 'wrap', gap: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 8px',
                          borderRadius: 'var(--radius-full)',
                          background: tc.bg, color: tc.text, border: '1px solid ' + tc.border,
                        }}>
                          {ch.record_type}
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {relTime(ch.detected_at)}
                        </span>
                      </div>
                      <span style={{ fontSize: 10, color: 'var(--text-muted)',
                                     fontFamily: 'var(--font-mono)' }}>
                        {ch.detected_at ? new Date(ch.detected_at).toLocaleString() : '-'}
                      </span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 20px 1fr', gap: 8,
                                  alignItems: 'start' }}>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--danger)',
                                      textTransform: 'uppercase', letterSpacing: '0.05em',
                                      marginBottom: 3 }}>Before</div>
                        <div style={{
                          fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-secondary)',
                          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.18)',
                          borderRadius: 'var(--radius-xs)', padding: '4px 7px',
                          wordBreak: 'break-all', lineHeight: 1.5, maxHeight: 64, overflow: 'auto',
                        }}>
                          {ch.old_value || '-'}
                        </div>
                      </div>
                      <div style={{ paddingTop: 22, textAlign: 'center',
                                    color: 'var(--text-muted)', fontSize: 13, fontWeight: 700 }}>→</div>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--success)',
                                      textTransform: 'uppercase', letterSpacing: '0.05em',
                                      marginBottom: 3 }}>After</div>
                        <div style={{
                          fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-secondary)',
                          background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.18)',
                          borderRadius: 'var(--radius-xs)', padding: '4px 7px',
                          wordBreak: 'break-all', lineHeight: 1.5, maxHeight: 64, overflow: 'auto',
                        }}>
                          {ch.new_value || '-'}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Expandable detail row ──────────────────────────────────────────────────
function DetailRow({ monitor, colCount, onEditDkim }) {
  var spfVal   = monitor.spf_current || monitor.spf_expected;
  var mxVal    = monitor.mx_current || monitor.mx_expected;
  var dmarcVal = monitor.dmarc_current || monitor.dmarc_expected;
  var dkimVal  = monitor.dkim_current || monitor.dkim_expected;

  var spfSt   = analyzeSpf(spfVal, monitor.spf_status);
  var mxSt    = analyzeMx(mxVal, monitor.mx_status);
  var dmarcSt = analyzeDmarc(dmarcVal, monitor.dmarc_status);
  var dkimSt  = analyzeDkim(dkimVal, monitor.dkim_status);

  // Use stored labels from backend when available
  var records = [
    {
      label: 'SPF', current: spfVal, expected: BASELINE.spf, status: spfSt,
      hint: 'TXT record at root domain',
      message: monitor.spf_label,
    },
    {
      label: 'DKIM', current: dkimVal, expected: monitor.dkim_expected_value || 'Auto-detected per domain',
      status: dkimSt,
      hint: monitor.dkim_selector ? 'Selector: ' + monitor.dkim_selector : 'Selector: auto-detected',
      message: monitor.dkim_label,
    },
    {
      label: 'DMARC', current: dmarcVal, expected: BASELINE.dmarc, status: dmarcSt,
      hint: '_dmarc.' + monitor.domain,
      message: monitor.dmarc_label,
    },
    {
      label: 'MX', current: mxVal, expected: BASELINE.mx.join(', '), status: mxSt,
      hint: 'Mail exchange records',
      message: monitor.mx_label,
    },
  ];

  function msgColor(st) {
    if (st === 'match' || st === 'found') return 'var(--success)';
    if (st === 'warning') return 'var(--warning)';
    return 'var(--danger)';
  }

  return (
    <tr>
      <td colSpan={colCount} style={{ padding: 0, background: 'var(--bg-table-head)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ padding: '16px 20px' }}>
          {/* Edit DKIM button */}
          <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={function() { onEditDkim(monitor); }}
              style={{ fontSize: 12 }}
            >
              Edit DKIM
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12 }}>
            {records.map(function(rec) {
              return (
                <div key={rec.label} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '12px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: '12px', color: 'var(--text-primary)', letterSpacing: '0.04em' }}>{rec.label}</span>
                    <StatusCell status={rec.status} />
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: 6 }}>{rec.hint}</div>

                  {/* Validation message */}
                  {rec.message && (
                    <div style={{ fontSize: '11px', color: msgColor(rec.status), marginBottom: 8, fontWeight: 500, lineHeight: 1.4 }}>
                      {rec.message}
                    </div>
                  )}

                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Current value</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: rec.current ? 'var(--text-primary)' : 'var(--danger)', wordBreak: 'break-all', lineHeight: 1.55, background: 'var(--bg-input)', borderRadius: 'var(--radius-xs)', padding: '5px 7px', maxHeight: 80, overflow: 'auto' }}>
                      {rec.current || 'Not found'}
                    </div>
                  </div>
                  {rec.status !== 'match' && rec.status !== 'found' && rec.expected && (
                    <div>
                      <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Expected value</div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--success)', wordBreak: 'break-all', lineHeight: 1.55, background: 'var(--success-subtle)', borderRadius: 'var(--radius-xs)', padding: '5px 7px', maxHeight: 80, overflow: 'auto' }}>
                        {rec.expected}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* DNS Change History */}
          <HistorySection monitorId={monitor.id} />
        </div>
      </td>
    </tr>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
function DNSMonitor({ token, user }) {
  var [monitors, setMonitors]       = useState([]);
  var [loading, setLoading]         = useState(true);
  var [showModal, setShowModal]     = useState(false);
  var [modalTab, setModalTab]       = useState('single');
  var [domain, setDomain]           = useState('');
  var [dkimSelector, setDkimSelector]   = useState('');
  var [dkimExpected, setDkimExpected]   = useState('');
  var [bulkText, setBulkText]       = useState('');
  var [csvRecords, setCsvRecords]   = useState([]);
  var [importing, setImporting]     = useState(false);
  var [importResult, setImportResult] = useState(null);
  var [error, setError]             = useState('');
  var [searchTerm, setSearchTerm]   = useState('');
  var [selectedIds, setSelectedIds] = useState([]);
  var [checkingAll, setCheckingAll] = useState(false);
  var [checkProgress, setCheckProgress] = useState({ current: 0, total: 0 });
  var [checkingId, setCheckingId] = useState(null);
  var [expandedId, setExpandedId]   = useState(null);
  var [statusFilter, setStatusFilter] = useState('');
  var [editDkim, setEditDkim]       = useState(null); // { monitorId, selector, expected }

  var fetchMonitors = useCallback(async function() {
    try {
      var res = await api.get('/dns');
      setMonitors(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch DNS monitors:', err);
      setLoading(false);
    }
  }, []);

  useEffect(function() {
    fetchMonitors();
    var interval = setInterval(fetchMonitors, 30000); // Refresh every 30 seconds
    return function() { clearInterval(interval); };
  }, [fetchMonitors]);

  var handleAddSingle = async function(e) {
    e.preventDefault();
    setError('');
    try {
      await api.post('/dns', {
        domain: domain,
        dkim_selector: dkimSelector || null,
        dkim_expected_value: dkimExpected || null,
      });
      setShowModal(false);
      setDomain('');
      setDkimSelector('');
      setDkimExpected('');
      fetchMonitors();
      setTimeout(fetchMonitors, 6000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add domain');
    }
  };

  var handleBulkImport = async function(e) {
    e.preventDefault();
    setError('');
    setImportResult(null);
    var domains = bulkText.split('\n').map(function(d) { return d.trim(); }).filter(Boolean);
    if (domains.length === 0) { setError('Please enter at least one domain'); return; }

    setImporting(true);
    var added = [];
    var errors = [];
    for (var i = 0; i < domains.length; i++) {
      try {
        await api.post('/dns', { domain: domains[i] });
        added.push(domains[i]);
      } catch (err) {
        errors.push(domains[i] + ': ' + (err.response?.data?.error || 'Failed'));
      }
    }
    setImportResult({ added: added, errors: errors });
    setBulkText('');
    setImporting(false);
    fetchMonitors();
    setTimeout(fetchMonitors, 8000);
  };

  function parseCSV(text) {
    var lines = text.trim().split('\n');
    var records = [];
    var start = 0;
    if (lines[0] && lines[0].toLowerCase().includes('domain')) {
      start = 1;
    }
    for (var i = start; i < lines.length; i++) {
      var line = lines[i].trim();
      if (!line) continue;
      var parts = line.split(',');
      records.push({
        domain: (parts[0] || '').trim(),
        dkim_selector: (parts[1] || '').trim() || null,
        dkim_expected_value: (parts[2] || '').trim() || null,
      });
    }
    return records.filter(function(r) { return r.domain; });
  }

  var handleCsvChange = function(e) {
    var text = e.target.value;
    setCsvRecords(parseCSV(text));
  };

  var handleCsvImport = async function() {
    if (csvRecords.length === 0) { setError('No valid records to import'); return; }
    setError('');
    setImportResult(null);
    setImporting(true);
    try {
      var res = await api.post('/dns/import-csv', { records: csvRecords });
      setImportResult(res.data);
      setCsvRecords([]);
      fetchMonitors();
      setTimeout(fetchMonitors, 8000);
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  var handleSaveEditDkim = async function() {
    if (!editDkim) return;
    try {
      await api.put('/dns/' + editDkim.monitorId, {
        dkim_selector: editDkim.selector || null,
        dkim_expected_value: editDkim.expected || null,
      });
      setEditDkim(null);
      fetchMonitors();
    } catch (err) {
      alert('Failed to update DKIM: ' + (err.response?.data?.error || err.message));
    }
  };

  var handleDelete = async function(id) {
    if (!window.confirm('Delete this domain?')) return;
    try {
      await api.delete('/dns/' + id);
      fetchMonitors();
    } catch (err) {
      console.error('Failed to delete monitor:', err);
    }
  };

  var handleBulkDelete = async function() {
    if (!window.confirm('Delete ' + selectedIds.length + ' selected domain(s)? This cannot be undone.')) return;
    try {
      await api.post('/dns/bulk-delete', { ids: selectedIds });
      setSelectedIds([]);
      fetchMonitors();
    } catch (err) {
      alert('Failed to delete: ' + (err.response?.data?.error || err.message));
    }
  };

  var handleCheckOne = async function(id) {
    setCheckingId(id);
    try {
      await api.post('/dns/check/' + id);
      await fetchMonitors();
    } catch (err) {
      alert('DNS check failed');
    } finally {
      setCheckingId(null);
    }
  };

  var handleCheckAll = async function() {
    setCheckingAll(true);
    var allMonitors = monitors.filter(function(m) { return m.is_active !== false; });
    setCheckProgress({ current: 0, total: allMonitors.length });
    try {
      for (var i = 0; i < allMonitors.length; i++) {
        setCheckProgress({ current: i + 1, total: allMonitors.length });
        try {
          await api.post('/dns/check/' + allMonitors[i].id);
        } catch (e) {}
        if ((i + 1) % 5 === 0 || i === allMonitors.length - 1) {
          await fetchMonitors();
        }
      }
    } catch (err) {
      alert('DNS check failed');
    } finally {
      setCheckingAll(false);
      setCheckProgress({ current: 0, total: 0 });
      fetchMonitors();
    }
  };

  var toggleSelect = function(id) {
    setSelectedIds(function(prev) {
      return prev.includes(id) ? prev.filter(function(x) { return x !== id; }) : prev.concat([id]);
    });
  };

  var toggleSelectAll = function() {
    setSelectedIds(selectedIds.length === filteredMonitors.length ? [] : filteredMonitors.map(function(m) { return m.id; }));
  };

  // Helper to get score as 0-4
  function getScore4(m) {
    var s = m.health_score;
    if (s === undefined || s === null) return null;
    if (s > 4) return Math.round(s / 25);
    return s;
  }

  var filteredMonitors = monitors.filter(function(m) {
    var matchSearch = m.domain.toLowerCase().includes(searchTerm.toLowerCase());
    var matchStatus = true;
    if (statusFilter) {
      var score = getScore4(m);
      if (statusFilter === 'full') matchStatus = score !== null && score >= 4;
      else if (statusFilter === 'partial') matchStatus = score !== null && score > 0 && score < 4;
      else if (statusFilter === 'none') matchStatus = score === 0 || score === null;
    }
    return matchSearch && matchStatus;
  });

  var canEdit = user?.role === 'admin' || user?.role === 'editor';
  var activeCount  = monitors.filter(function(m) { return (getScore4(m) ?? 0) >= 4; }).length;
  var warningCount = monitors.filter(function(m) { var s = getScore4(m); return s !== null && s > 0 && s < 4; }).length;
  var issueCount   = monitors.filter(function(m) { var s = getScore4(m); return s === 0 || s === null; }).length;
  var colCount = canEdit ? 8 : 7;

  if (loading) {
    return <div className="loading"><div className="spinner" /><span>Loading email records…</span></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-header-top">
          <div>
            <h1>Email Records</h1>
            <p>SPF, DKIM, DMARC and MX records for <strong style={{ color: 'var(--text-primary)' }}>{monitors.length}</strong> domains</p>
          </div>
          {canEdit && (
            <div className="page-actions">
              <button className="btn btn-primary" onClick={handleCheckAll} disabled={checkingAll} style={checkingAll ? { background: "linear-gradient(90deg, var(--primary) " + (checkProgress.total > 0 ? Math.round((checkProgress.current / checkProgress.total) * 100) : 0) + "%, transparent " + (checkProgress.total > 0 ? Math.round((checkProgress.current / checkProgress.total) * 100) : 0) + "%)", border: "1px solid var(--primary)", transition: "all 0.3s ease" } : {}}>
                <IconRefresh /> {checkingAll ? checkProgress.current + '/' + checkProgress.total : 'Check All'}
              </button>
              <button className="btn btn-primary" onClick={function() { setShowModal(true); setError(''); setImportResult(null); }}>
                <IconPlus /> Add Domains
              </button>
            </div>
          )}
        </div>
      </div>

      {monitors.length > 0 && (
        <div className="cards-grid" style={{ marginBottom: 20, gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
          {[
            { label: 'Domains',        val: monitors.length, color: 'var(--info)',    filter: '' },
            { label: 'Fully OK',       val: activeCount,      color: 'var(--success)', filter: 'full' },
            { label: 'Partial Issues', val: warningCount,     color: 'var(--warning)', filter: 'partial' },
            { label: 'Failing',        val: issueCount,       color: 'var(--danger)',  filter: 'none' },
          ].map(function(s) {
            return (
              <div key={s.label} className="card" style={{ cursor: 'pointer' }}
                   onClick={function() { setStatusFilter(function(prev) { return prev === s.filter ? '' : s.filter; }); }}>
                <div className="card-title" style={{ marginBottom: 8 }}>{s.label}</div>
                <div className="card-value" style={{ fontSize: 26, color: s.color }}>{s.val}</div>
                {statusFilter === s.filter && s.filter !== undefined && (
                  <div style={{ fontSize: '10px', color: 'var(--primary)', fontWeight: 600, marginTop: 4 }}>Filtering ×</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="table-container">
        <div className="table-header">
          <h3>Domains</h3>
          <div className="table-toolbar">
            <div className="search-input-wrap">
              <IconSearch />
              <input type="text" placeholder="Search domains…" value={searchTerm} onChange={function(e) { setSearchTerm(e.target.value); }} />
            </div>
            {canEdit && filteredMonitors.length > 0 && (
              <button className="btn btn-secondary btn-sm" onClick={toggleSelectAll}>
                {selectedIds.length === filteredMonitors.length ? 'Deselect all' : 'Select all'}
              </button>
            )}
            {canEdit && selectedIds.length > 0 && (
              <button className="btn btn-danger btn-sm" onClick={handleBulkDelete} style={{ marginLeft: 6 }}>
                <IconTrash /> Delete {selectedIds.length} Selected
              </button>
            )}
          </div>
        </div>

        {filteredMonitors.length === 0 ? (
          <div className="table-empty">
            <div className="table-empty-icon"><IconGlobe /></div>
            <div className="table-empty-title">{searchTerm || statusFilter ? 'No matching domains' : 'No domains yet'}</div>
            <div className="table-empty-desc">{searchTerm || statusFilter ? 'Try a different search or clear the filter' : 'Add domains to start monitoring DNS records'}</div>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                {canEdit && <th style={{ width: 36 }}></th>}
                <th>Domain</th>
                <th>SPF</th>
                <th>DKIM</th>
                <th>DMARC</th>
                <th>MX</th>
                <th style={{ width: 60 }}>Score</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMonitors.map(function(monitor) {
                var isExpanded = expandedId === monitor.id;
                var checked = hasBeenChecked(monitor);

                var spfVal   = monitor.spf_current || monitor.spf_expected;
                var dkimVal  = monitor.dkim_current || monitor.dkim_expected;
                var dmarcVal = monitor.dmarc_current || monitor.dmarc_expected;
                var mxVal    = monitor.mx_current || monitor.mx_expected;

                var spfSt   = checked ? analyzeSpf(spfVal, monitor.spf_status)     : 'pending';
                var dkimSt  = checked ? analyzeDkim(dkimVal, monitor.dkim_status)   : 'pending';
                var dmarcSt = checked ? analyzeDmarc(dmarcVal, monitor.dmarc_status) : 'pending';
                var mxSt    = checked ? analyzeMx(mxVal, monitor.mx_status)         : 'pending';

                return (
                  <React.Fragment key={monitor.id}>
                    <tr style={{ background: isExpanded ? 'var(--bg-table-row-hover)' : undefined }}>
                      {canEdit && (
                        <td style={{ padding: '8px 12px' }}>
                          <input type="checkbox" checked={selectedIds.includes(monitor.id)} onChange={function() { toggleSelect(monitor.id); }} />
                        </td>
                      )}
                      <td>
                        <button onClick={function() { setExpandedId(isExpanded ? null : monitor.id); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', padding: 0 }}>
                          <IconChevron open={isExpanded} />
                          {monitor.domain}
                        </button>
                      </td>
                      <td><StatusCell status={spfSt} /></td>
                      <td><StatusCell status={dkimSt} /></td>
                      <td><StatusCell status={dmarcSt} /></td>
                      <td><StatusCell status={mxSt} /></td>
                      <td>
                        {checked ? <HealthScore score={monitor.health_score} /> : <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Pending…</span>}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {canEdit && <button className="btn btn-secondary btn-sm" onClick={function() { handleCheckOne(monitor.id); }}><span style={checkingId === monitor.id ? { display: "inline-flex", animation: "spin 1s linear infinite" } : { display: "inline-flex" }}><IconRefresh /></span></button>}
                          {canEdit && <button className="btn btn-danger btn-sm" onClick={function() { handleDelete(monitor.id); }}><IconTrash /></button>}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <DetailRow
                        monitor={monitor}
                        colCount={colCount}
                        onEditDkim={function(mon) {
                          setEditDkim({ monitorId: mon.id, selector: mon.dkim_selector || '', expected: mon.dkim_expected_value || '' });
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        {filteredMonitors.length > 0 && (
          <div className="table-footer">
            Showing {filteredMonitors.length} of {monitors.length} domains
            {statusFilter && (
              <button onClick={function() { setStatusFilter(''); }}
                style={{ marginLeft: 10, color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                Clear filter
              </button>
            )}
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={function() { setShowModal(false); }}>
          <div className="modal" style={{ maxWidth: 540 }} onClick={function(e) { e.stopPropagation(); }}>
            <div className="modal-header">
              <h3>Add Domains</h3>
              <button className="modal-close" onClick={function() { setShowModal(false); }}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', gap: 4, background: 'var(--bg-table-head)', borderRadius: 'var(--radius-md)', padding: 4, marginBottom: 18 }}>
                {[['single', 'Single Domain'], ['bulk', 'Bulk Import'], ['csv', 'CSV Import']].map(function(item) {
                  var key = item[0]; var label = item[1];
                  return (
                    <button key={key} onClick={function() { setModalTab(key); setError(''); setImportResult(null); }}
                      style={{ flex: 1, padding: '6px 12px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', borderRadius: 'var(--radius-sm)',
                        background: modalTab === key ? 'var(--bg-card)' : 'transparent',
                        color: modalTab === key ? 'var(--text-primary)' : 'var(--text-muted)',
                        boxShadow: modalTab === key ? 'var(--shadow-sm)' : 'none',
                        fontFamily: 'var(--font)', transition: 'all 0.15s ease' }}>
                      {label}
                    </button>
                  );
                })}
              </div>

              {error && <div className="alert alert-error">{error}</div>}
              {importResult && (
                <div className="alert alert-success">
                  Added <strong>{importResult.added?.length || 0}</strong> domain(s).
                  {importResult.skipped?.length > 0 && ' ' + importResult.skipped.length + ' already existed.'}
                  {importResult.errors?.length > 0 && ' ' + importResult.errors.length + ' error(s).'}
                </div>
              )}

              {modalTab === 'single' ? (
                <form onSubmit={handleAddSingle}>
                  <div className="form-group">
                    <label>Domain Name <span className="label-hint">*required</span></label>
                    <input type="text" value={domain} onChange={function(e) { setDomain(e.target.value); }} required placeholder="example.com" autoFocus />
                    <div className="form-hint">DNS records (SPF, DKIM, DMARC, MX) are fetched automatically from Google DNS.</div>
                  </div>
                  <div className="form-group">
                    <label>DKIM Selector <span className="label-hint">optional</span></label>
                    <input type="text" value={dkimSelector} onChange={function(e) { setDkimSelector(e.target.value); }} placeholder="e.g. google, selector1, default" />
                    <div className="form-hint">Leave empty to auto-detect from common selectors.</div>
                  </div>
                  <div className="form-group">
                    <label>DKIM Expected Value <span className="label-hint">optional</span></label>
                    <textarea value={dkimExpected} onChange={function(e) { setDkimExpected(e.target.value); }} placeholder="v=DKIM1; k=rsa; p=MIGfMA0G..." rows={3} />
                    <div className="form-hint">Leave empty to just check if DKIM exists.</div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={function() { setShowModal(false); }}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Add &amp; Check DNS</button>
                  </div>
                </form>
              ) : modalTab === 'bulk' ? (
                <form onSubmit={handleBulkImport}>
                  <div className="form-group">
                    <label>Domains <span className="label-hint">one per line</span></label>
                    <textarea value={bulkText} onChange={function(e) { setBulkText(e.target.value); }} placeholder={'domain1.com\ndomain2.com\ndomain3.com'} rows={10} style={{ minHeight: 200 }} autoFocus />
                    <div className="form-hint">{bulkText.split('\n').filter(function(d) { return d.trim(); }).length} domain(s) entered.</div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={function() { setShowModal(false); }}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={importing}>{importing ? 'Importing…' : 'Import All Domains'}</button>
                  </div>
                </form>
              ) : (
                <div>
                  <div className="form-group">
                    <label>Paste CSV or Upload File <span className="label-hint">domain,dkim_selector,dkim_value</span></label>
                    <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                      <button type="button" className="btn btn-secondary" onClick={function() { document.getElementById("csv-file-input").click(); }} style={{ fontSize: 12 }}>
                        Upload CSV File
                      </button>
                      <input
                        id="csv-file-input"
                        type="file"
                        accept=".csv,.txt"
                        style={{ display: "none" }}
                        onChange={function(e) {
                          var f = e.target.files[0];
                          if (!f) return;
                          var reader = new FileReader();
                          reader.onload = function(ev) {
                            var text = ev.target.result;
                            var textarea = document.querySelector("textarea");
                            if (textarea) { textarea.value = text; }
                            handleCsvChange({ target: { value: text } });
                          };
                          reader.readAsText(f);
                          e.target.value = "";
                        }}
                      />
                    </div>
                    <textarea
                      onChange={handleCsvChange}
                      placeholder={'domain,dkim_selector,dkim_value\nexample1.com,google,v=DKIM1; k=rsa; p=ABC...\nexample2.com,,'}
                      rows={6}
                      autoFocus
                      style={{ minHeight: 140 }}
                    />
                    <div className="form-hint">First row is treated as a header if it contains "domain". DKIM columns are optional.</div>
                  </div>

                  {csvRecords.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>
                        {csvRecords.length} domain(s) ready to import
                      </div>
                      <div style={{ maxHeight: 160, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
                        <table style={{ width: '100%', fontSize: 11 }}>
                          <thead>
                            <tr style={{ background: 'var(--bg-table-head)' }}>
                              <th style={{ padding: '6px 10px', textAlign: 'left' }}>Domain</th>
                              <th style={{ padding: '6px 10px', textAlign: 'left' }}>DKIM Selector</th>
                              <th style={{ padding: '6px 10px', textAlign: 'left' }}>DKIM Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {csvRecords.map(function(r, idx) {
                              return (
                                <tr key={idx} style={{ borderTop: '1px solid var(--border)' }}>
                                  <td style={{ padding: '5px 10px', fontFamily: 'var(--font-mono)' }}>{r.domain}</td>
                                  <td style={{ padding: '5px 10px', color: r.dkim_selector ? 'var(--text-primary)' : 'var(--text-muted)' }}>{r.dkim_selector || '-'}</td>
                                  <td style={{ padding: '5px 10px', color: r.dkim_expected_value ? 'var(--text-primary)' : 'var(--text-muted)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.dkim_expected_value ? r.dkim_expected_value.substring(0, 30) + '…' : '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={function() { setShowModal(false); }}>Cancel</button>
                    <button type="button" className="btn btn-primary" onClick={handleCsvImport} disabled={importing || csvRecords.length === 0}>
                      {importing ? 'Importing…' : 'Import ' + csvRecords.length + ' Domain(s)'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Edit DKIM modal */}
      {editDkim && (
        <div className="modal-overlay" onClick={function() { setEditDkim(null); }}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={function(e) { e.stopPropagation(); }}>
            <div className="modal-header">
              <h3>Edit DKIM Settings</h3>
              <button className="modal-close" onClick={function() { setEditDkim(null); }}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>DKIM Selector <span className="label-hint">optional</span></label>
                <input
                  type="text"
                  value={editDkim.selector}
                  onChange={function(e) { setEditDkim(function(prev) { return Object.assign({}, prev, { selector: e.target.value }); }); }}
                  placeholder="e.g. google, selector1, default"
                  autoFocus
                />
                <div className="form-hint">Leave empty to auto-detect from common selectors.</div>
              </div>
              <div className="form-group">
                <label>DKIM Expected Value <span className="label-hint">optional</span></label>
                <textarea
                  value={editDkim.expected}
                  onChange={function(e) { setEditDkim(function(prev) { return Object.assign({}, prev, { expected: e.target.value }); }); }}
                  placeholder="v=DKIM1; k=rsa; p=MIGfMA0G..."
                  rows={4}
                />
                <div className="form-hint">Leave empty to just check if DKIM exists without comparing value.</div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={function() { setEditDkim(null); }}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={handleSaveEditDkim}>Save &amp; Re-check</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DNSMonitor;
